import { JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TestModeService } from '../../../../../../services/test-mode.service';
import { CesIconComponent } from '../../../../../ui/ces-icon/ces-icon.component';
import { CesListSkeletonComponent } from '../../../../../ui/skeletons/list-skeleton.component';
import { LoadingSpinnerComponent } from '../sub-components/ces-loading-spinner/loading-spinner.component';
import { LogContentComponent } from '../sub-components/ces-log-content/log-content.component';
import { tryTranslate } from '../sub-components/ces-log-content/i18n.utils';
import { CampaignHistoryService, CampaignLogEntry } from '../sub-services/campaign-history.service';
import { CampaignPageStore } from '../sub-services/campaign-page.store';

import { CesTooltipAutoDismissDirective } from '../../../../../../directives/ces-tooltip-auto-dismiss.directive';
@Component({
  selector: 'ces-campaign-history',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesTooltipAutoDismissDirective,
    JsonPipe,
    AccordionModule,
    TagModule,
    DividerModule,
    TooltipModule,
    ButtonModule,
    TranslocoModule,
    LogContentComponent,
    LoadingSpinnerComponent,
    CesListSkeletonComponent,
    CesIconComponent,
  ],
  templateUrl: './campaign-history.component.html',
  styles: [``],
})
export class CesCampaignHistoryComponent {
  readonly pageStore = inject(CampaignPageStore);
  readonly hist = inject(CampaignHistoryService);
  readonly t = inject(TestModeService);
  private readonly translate = inject(TranslocoService);
  readonly chronologicalHistory = computed(() => {
    const allLogs = this.hist.campaignHistory();
    if (!allLogs.length) return [];

    return allLogs.slice().sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  });

  readonly openAccordions = signal<number[]>([]);

  getLogTitle(log: CampaignLogEntry): string {
    if (!log.comment) return this.translate.translate('history.campaignAction');
    return tryTranslate(this.translate, log.comment);
  }

  getLogDetailCount(log: CampaignLogEntry): number {
    return log.details?.length ?? 0;
  }

  getModificationTooltip(log: CampaignLogEntry): string {
    const count = this.getLogDetailCount(log);
    const key = count > 1 ? 'history.modifications' : 'history.modification';
    return `${count} ${this.translate.translate(key)}`;
  }

  getUserName(id: number): string {
    if (!id) return this.translate.translate('history.unknownUser');
    const user = this.hist.getUserName(id);
    if (user) {
      return `${user.firstName} ${user.lastName.toUpperCase()}.`;
    }
    return this.translate.translate('history.userWithId', { id });
  }

  formatLogDate(dateString: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getLastModificationDate(): string {
    const lastLog = this.chronologicalHistory()[0];
    if (!lastLog) return this.translate.translate('history.noModifications');
    return this.formatLogDate(lastLog.date);
  }

  getTotalModificationCount(): number {
    const logs = this.chronologicalHistory();
    return logs.reduce((total, log) => total + (log.details?.length ?? 0), 0);
  }

  toggleAllAccordions(event: Event): void {
    event.stopPropagation();

    const allLogIds = this.chronologicalHistory().map(log => log.id);
    const currentOpenIds = this.openAccordions();

    if (currentOpenIds.length === allLogIds.length) {
      this.openAccordions.set([]);
    } else {
      this.openAccordions.set(allLogIds);
    }
  }
}
